/**
 * ETP Exporter
 *
 * Utility class to build and export ETP (EON Treatment Plan) data
 * for the EON Dental 3D Viewer.
 *
 * @version 2.0
 * @author EON Dental
 */

import { stringifyOrdered } from "../../../helpers/orderedJson.ts";
import type {
  ETP,
  ETPModels,
  ETPTeethModel,
  GingivasModel,
  BonesModel,
  Tooth,
  ToothAnatomy,
  DrcModel,
  FDIToothID,
  NumberingSystem,
  TreatmentStep,
  ToothStepInfo,
  PonticStepInfo,
  AttachmentModel,
  PonticModel,
  BoltonAnalysis,
  Matrix4,
  Matrix4Flat,
  Vector3,
} from "./ETP";

// ============================================
// Helper Functions
// ============================================

/**
 * Convert Uint8Array to base64 string
 */
export function uint8ArrayToBase64(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.byteLength; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const data = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    data[i] = binary.charCodeAt(i);
  }
  return data;
}

/**
 * Create a DrcModel from base64 string directly
 */
export function createDrcModel(base64Data: string): DrcModel {
  return { data: base64Data };
}

/**
 * Create DrcModel from base64 string
 */
export function drcFromBase64(base64: string): DrcModel {
  return { data: base64 };
}

/**
 * Create default ToothAnatomy
 */
export function createDefaultAnatomy(): ToothAnatomy {
  return {
    centerOfRotation: [0, 0, 0],
    axes: {
      longAxis: [0, 1, 0],
      facialAxis: [0, 0, 1],
    },
  };
}

/**
 * Create a Tooth object
 */
export function createTooth(
  crownData: DrcModel,
  anatomy?: ToothAnatomy,
  rootData?: DrcModel,
  simulatedRootData?: DrcModel,
): Tooth {
  const tooth: Tooth = {
    crown: crownData,
    anatomy: anatomy ?? createDefaultAnatomy(),
  };

  if (rootData) {
    tooth.root = rootData;
  }
  if (simulatedRootData) {
    tooth.simulatedRoot = simulatedRootData;
  }

  return tooth;
}

// ============================================
// ETPExporter Class
// ============================================

export class ETPExporter {
  private version: number;
  private numberingSystem: NumberingSystem;
  private teeth: ETPTeethModel;
  private gingiva: GingivasModel;
  private bones: BonesModel;
  private attachments: AttachmentModel[];
  private pontics: PonticModel[];
  private treatmentPlan: TreatmentStep[];
  private boltonAnalysis?: BoltonAnalysis;

  constructor(version: number = 3) {
    this.version = version;
    this.numberingSystem = "FDI";
    this.teeth = {};
    this.gingiva = {};
    this.bones = {};
    this.attachments = [];
    this.pontics = [];
    this.treatmentPlan = [];
  }

  // ----- Builder Methods -----

  /**
   * Set the version number
   */
  setVersion(version: number): this {
    this.version = version;
    return this;
  }

  /**
   * Add a tooth with full parameters
   */
  addTooth(
    toothId: FDIToothID,
    crownData: DrcModel,
    anatomy?: ToothAnatomy,
    rootData?: DrcModel,
    simulatedRootData?: DrcModel,
  ): this {
    this.teeth[toothId] = createTooth(
      crownData,
      anatomy,
      rootData ? rootData : undefined,
      simulatedRootData ? simulatedRootData : undefined,
    );
    return this;
  }

  /**
   * Add a Tooth object directly
   */
  addToothObject(toothId: FDIToothID, tooth: Tooth): this {
    this.teeth[toothId] = tooth;
    return this;
  }

  /**
   * Set maxillary (upper) gingiva
   */
  setMaxillaryGingiva(data: DrcModel): this {
    this.gingiva.maxillary = data;
    return this;
  }

  /**
   * Set mandibular (lower) gingiva
   */
  setMandibularGingiva(data: DrcModel): this {
    this.gingiva.mandibular = data;
    return this;
  }

  /**
   * Set maxillary (upper) bone
   */
  setMaxillaryBone(data: DrcModel): this {
    this.bones.maxillaryBone = data;
    return this;
  }

  /**
   * Set mandibular (lower) bone
   */
  setMandibularBone(data: DrcModel): this {
    this.bones.mandibularBone = data;
    return this;
  }

  /**
   * Set nerve model
   */
  setNerve(data: DrcModel): this {
    this.bones.nerve = data;
    return this;
  }

  /**
   * Set sinus model
   */
  setSinus(data: DrcModel): this {
    this.bones.sinus = data;
    return this;
  }

  /**
   * Set airway model
   */
  setAirway(data: DrcModel): this {
    this.bones.airway = data;
    return this;
  }

  /**
   * Add an attachment model
   */
  addAttachment(type: string, id: string, modelData: DrcModel): this {
    this.attachments.push({
      type,
      id,
      model: modelData,
    });
    return this;
  }

  /**
   * Add a pontic model
   */
  addPontic(id: string, crownData: DrcModel): this {
    this.pontics.push({
      id,
      crown: crownData,
    });
    return this;
  }

  /**
   * Add a treatment step
   */
  addTreatmentStep(step: TreatmentStep): this {
    this.treatmentPlan.push(step);
    return this;
  }

  /**
   * Set the full treatment plan
   */
  setTreatmentPlan(steps: TreatmentStep[]): this {
    this.treatmentPlan = steps;
    return this;
  }

  /**
   * Set Bolton analysis data
   */
  setBoltonAnalysis(analysis: BoltonAnalysis): this {
    this.boltonAnalysis = analysis;
    return this;
  }

  /**
   * Remove a tooth
   */
  removeTooth(toothId: FDIToothID): this {
    delete this.teeth[toothId];
    return this;
  }

  /**
   * Clear all teeth
   */
  clearTeeth(): this {
    this.teeth = {};
    return this;
  }

  /**
   * Clear all gingiva
   */
  clearGingiva(): this {
    this.gingiva = {};
    return this;
  }

  /**
   * Clear all bones
   */
  clearBones(): this {
    this.bones = {};
    return this;
  }

  /**
   * Clear all attachments
   */
  clearAttachments(): this {
    this.attachments = [];
    return this;
  }

  /**
   * Clear all pontics
   */
  clearPontics(): this {
    this.pontics = [];
    return this;
  }

  /**
   * Clear treatment plan
   */
  clearTreatmentPlan(): this {
    this.treatmentPlan = [];
    return this;
  }

  /**
   * Clear all data
   */
  clear(): this {
    this.clearTeeth();
    this.clearGingiva();
    this.clearBones();
    this.clearAttachments();
    this.clearPontics();
    this.clearTreatmentPlan();
    this.boltonAnalysis = undefined;
    return this;
  }

  // ----- Getters -----

  /**
   * Get tooth by ID
   */
  getTooth(toothId: FDIToothID): Tooth | undefined {
    return this.teeth[toothId];
  }

  /**
   * Check if tooth exists
   */
  hasTooth(toothId: FDIToothID): boolean {
    return toothId in this.teeth;
  }

  /**
   * Get all tooth IDs
   */
  getToothIds(): FDIToothID[] {
    return Object.keys(this.teeth) as FDIToothID[];
  }

  /**
   * Get tooth count
   */
  get toothCount(): number {
    return Object.keys(this.teeth).length;
  }

  /**
   * Get step count
   */
  get stepCount(): number {
    return this.treatmentPlan.length;
  }

  /**
   * Check if maxillary gingiva is set
   */
  hasMaxillaryGingiva(): boolean {
    return this.gingiva.maxillary !== undefined;
  }

  /**
   * Check if mandibular gingiva is set
   */
  hasMandibularGingiva(): boolean {
    return this.gingiva.mandibular !== undefined;
  }

  /**
   * Check if bones are set
   */
  hasBones(): boolean {
    return Object.keys(this.bones).length > 0;
  }

  // ----- Build Methods -----

  /**
   * Build the Models object
   */
  buildModels(): ETPModels {
    const models: ETPModels = {
      teeth: this.teeth,
      gingiva: this.gingiva,
    };

    if (Object.keys(this.bones).length > 0) {
      models.bones = this.bones;
    }
    if (this.attachments.length > 0) {
      models.attachments = this.attachments;
    }
    if (this.pontics.length > 0) {
      models.pontics = this.pontics;
    }

    return models;
  }

  /**
   * Build the final ETP object
   */
  build(): ETP {
    const etp: ETP = {
      version: this.version,
      numberingSystem: this.numberingSystem,
      models: this.buildModels(),
      treatmentPlan: this.treatmentPlan,
    };

    if (this.boltonAnalysis) {
      etp.boltonAnalysis = this.boltonAnalysis;
    }

    return etp;
  }

  // ----- Export Methods -----

  /**
   * Convert to JSON object (with base64 encoded data)
   * Matches ETP structure
   */
  toJSON(): Record<string, unknown> {
    // Convert teeth
    const teethJson: Record<string, unknown> = {};
    for (const [id, tooth] of Object.entries(this.teeth)) {
      const toothJson: Record<string, unknown> = {
        crown: tooth.crown,
        anatomy: tooth.anatomy,
      };
      if (tooth.root) {
        toothJson.root = tooth.root;
      }
      if (tooth.simulatedRoot) {
        toothJson.simulatedRoot = tooth.simulatedRoot;
      }
      teethJson[id] = toothJson;
    }

    // Convert gingiva
    const gingivaJson: Record<string, unknown> = {};
    if (this.gingiva.maxillary) {
      gingivaJson.maxillary = this.gingiva.maxillary;
    }
    if (this.gingiva.mandibular) {
      gingivaJson.mandibular = this.gingiva.mandibular;
    }

    // Convert bones
    const bonesJson: Record<string, unknown> = {};
    if (this.bones.maxillaryBone) {
      bonesJson.maxillaryBone = this.bones.maxillaryBone;
    }
    if (this.bones.mandibularBone) {
      bonesJson.mandibularBone = this.bones.mandibularBone;
    }
    if (this.bones.nerve) {
      bonesJson.nerve = this.bones.nerve;
    }
    if (this.bones.sinus) {
      bonesJson.sinus = this.bones.sinus;
    }
    if (this.bones.airway) {
      bonesJson.airway = this.bones.airway;
    }

    // Convert attachments
    const attachmentsJson = this.attachments.map(att => ({
      type: att.type,
      id: att.id,
      model: att.model,
    }));

    // Convert pontics
    const ponticsJson = this.pontics.map(pontic => ({
      id: pontic.id,
      crown: pontic.crown,
    }));

    // Build models object
    const modelsJson: Record<string, unknown> = {
      teeth: teethJson,
      gingiva: gingivaJson,
    };
    if (Object.keys(bonesJson).length > 0) {
      modelsJson.bones = bonesJson;
    }
    if (attachmentsJson.length > 0) {
      modelsJson.attachments = attachmentsJson;
    }
    if (ponticsJson.length > 0) {
      modelsJson.pontics = ponticsJson;
    }

    // Build final ETP object
    const etpJson: Record<string, unknown> = {
      version: this.version,
      numberingSystem: this.numberingSystem,
      models: modelsJson,
      treatmentPlan: this.treatmentPlan,
    };

    if (this.boltonAnalysis) {
      etpJson.boltonAnalysis = this.boltonAnalysis;
    }

    return etpJson;
  }

  /**
   * Convert to JSON string
   */
  stringify(pretty: boolean = false): string {
    return stringifyOrdered(this.toJSON(), pretty ? 2 : 0);
  }

  /**
   * Download as JSON file
   */
  download(filename: string = "etp_treatment_plan"): void {
    const jsonString = this.stringify(true);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.json`;
    a.click();

    URL.revokeObjectURL(url);

    console.log(`Downloaded: ${filename}.json`);
  }

  // ----- Summary -----

  /**
   * Get summary of contents
   */
  getSummary(): {
    version: number;
    toothCount: number;
    toothIds: FDIToothID[];
    stepCount: number;
    hasMaxillaryGingiva: boolean;
    hasMandibularGingiva: boolean;
    hasBones: boolean;
    attachmentCount: number;
    ponticCount: number;
    hasBoltonAnalysis: boolean;
  } {
    return {
      version: this.version,
      toothCount: this.toothCount,
      toothIds: this.getToothIds(),
      stepCount: this.stepCount,
      hasMaxillaryGingiva: this.hasMaxillaryGingiva(),
      hasMandibularGingiva: this.hasMandibularGingiva(),
      hasBones: this.hasBones(),
      attachmentCount: this.attachments.length,
      ponticCount: this.pontics.length,
      hasBoltonAnalysis: this.boltonAnalysis !== undefined,
    };
  }
}

// Default export
export default ETPExporter;
